import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Stethoscope } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t } from '../i18n';
import { Button, Input, Field, Alert } from '../components/ui';

/**
 * Where a professional creates their login.
 *
 * Admin records the professional first (Profesionales → Agregar), which leaves
 * a therapists row with no auth_user_id. Here that person sets their own
 * password; on first sign-in claim_therapist_profile() links the two by
 * matching their verified email.
 *
 * Deliberately separate from patient registration: a professional has no DNI,
 * emergency contact or obra social to declare, and asking for them would be
 * nonsense. It also means nobody can grant themselves professional access —
 * the claim only succeeds against a record admin already created.
 */

const ActivateProfessionalView = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const submit = async (event) => {
    event.preventDefault();
    setError(null);

    if (password.length < 8) { setError(t('register.passwordTooShort')); return; }
    if (password !== confirmPassword) { setError(t('register.passwordMismatch')); return; }

    setLoading(true);
    const { data, error: signUpError } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: { emailRedirectTo: window.location.origin },
      // No profile metadata: the therapists row already exists, created by
      // admin. Nothing here should look like a pending patient.
    });
    setLoading(false);

    if (signUpError) { setError(signUpError.message); return; }

    // If confirmation is disabled the session exists immediately, so link now.
    if (data.session) {
      await supabase.rpc('claim_therapist_profile');
      navigate('/', { replace: true });
      return;
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">{t('activate.sentTitle')}</h1>
          <p className="mt-2 text-sm text-slate-600">{t('activate.sentBody')}</p>
          <Button className="mt-6 w-full" onClick={() => navigate('/ingresar')}>
            {t('auth.login')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-indigo-900">{t('common.appName')}</h1>
          <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-slate-600">
            <Stethoscope size={15} />{t('activate.title')}
          </p>
        </div>

        <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <Alert tone="info">{t('activate.explanation')}</Alert>

          <div className="mt-4 space-y-4">
            {error && <Alert tone="danger">{error}</Alert>}

            <Field label={t('auth.email')} required hint={t('activate.emailHint')}>
              <Input
                type="email" value={email} autoComplete="email" required disabled={loading}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Field label={t('activate.choosePassword')} required hint={t('register.passwordHint')}>
              <Input
                type="password" value={password} autoComplete="new-password" required disabled={loading}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            <Field label={t('register.confirmPassword')} required>
              <Input
                type="password" value={confirmPassword} autoComplete="new-password" required disabled={loading}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </Field>

            <Button type="submit" loading={loading} className="w-full">
              {t('activate.submit')}
            </Button>
          </div>

          <p className="mt-6 border-t border-slate-100 pt-5 text-center text-sm text-slate-600">
            <Link to="/ingresar" className="font-semibold text-indigo-600 hover:underline">
              {t('activate.backToLogin')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ActivateProfessionalView;
